import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import {
  createClass,
  loadClassSafe,
  ZClassRow,
  ZClassUnsafe,
} from "src/business/classes";
import { makeClassInternalData } from "src/business/classes/makeClassInternalData";
import { makeClassRawData } from "src/business/classes/makeClassRawData";
import { loadClassifier, ZClassifier } from "src/business/classifier";
import {
  loadPrimarySpecForNode,
  ZSpecification,
} from "src/business/specification";
import { delay } from "src/common/delay";
import { RemoteData } from "src/common/RemoteData";
import { MfErrorsListStore } from "src/components/MForm/components/MfErrorsListView";
import { MFormStore } from "src/components/MForm/MFormStore";
import { submitMForm } from "src/components/MForm/submitMForm";
import { PageUrl } from "src/routes/PageUrl";
import { ZSidLabel } from "src/types";

export type NewClassData = {
  classifier: ZClassifier;
  schemes: ZSpecification[];
  parentClass?: ZClassRow;
};

export const newClassPageStore = makeAutoObservable({
  async init(classifierId: string, classId: string | null) {
    try {
      this.setClassifierId(classifierId);
      this.setData({ status: "wait" });

      const classifier = await loadClassifier(classifierId);
      const primarySchema = await loadPrimarySpecForNode(
        classId ?? classifierId,
      );
      const schemes = [primarySchema];
      const parentClass = classId
        ? await loadClassSafe(classId, schemes)
        : undefined;

      let parents: ZSidLabel[][] | undefined;
      if (parentClass) {
        parents = (parentClass.parents ?? []).map((list) => [...list]);
        const cls = { id: parentClass.id, label: parentClass.name };
        if (parents.length === 0) {
          parents[0] = [cls];
        } else {
          parents.forEach((p) => p.push(cls));
        }
      }

      const result = { classifier, parentClass, schemes };

      const { meta, data } = makeClassInternalData(
        {
          displayId: undefined as unknown as number,
          id: "",
          name: "",
          data: {},
          parents,
        },
        [primarySchema],
        parentClass?.id ?? classifierId,
      );
      this.setData({ status: "ready", result });
      await delay(1);

      this.form.initStd(meta, data);
      this.errorsStore.clear();
    } catch (error) {
      this.setData({ status: "error", error });
    }
  },
  get form(): MFormStore {
    return this.errorsStore.form;
  },
  errorsStore: new MfErrorsListStore(new MFormStore()),
  classifierId: "",
  setClassifierId(id: string) {
    this.classifierId = id;
  },
  data: { status: "none" } as RemoteData<NewClassData>,
  setData(newData: RemoteData<NewClassData>) {
    this.data = newData;
  },
  get breadcrumbs(): BreadcrumbItem[] | undefined {
    const { data } = this;
    if (data.status !== "ready") {
      return undefined;
    }
    return [
      { title: "Классификаторы", page: PageUrl.classifiers },
      {
        title: data.result.classifier.name,
        page: PageUrl.classes,
        params: { id: data.result.classifier.id },
      },
      { title: "Новый класс" },
    ];
  },

  async create(): Promise<ZClassUnsafe> {
    const { parentInfo, data } = this;
    if (!parentInfo) throw Error("Отсутствует информация о parent");
    if (data.status !== "ready") throw Error("Нет доступа к данным");
    const internalValues = this.form.fieldsValue;
    const rawData = makeClassRawData(internalValues, data.result.schemes);
    return submitMForm(
      () => createClass(parentInfo.id, rawData) as Promise<ZClassUnsafe>,
      this.errorsStore,
    );
  },

  get parentInfo(): ParentInfo | undefined {
    const { data } = this;
    if (data.status !== "ready") return undefined;
    const { classifier, parentClass } = data.result;
    if (parentClass) {
      return {
        id: parentClass.id,
        name: parentClass.name,
        type: "class",
      };
    }
    return {
      id: classifier.id,
      name: classifier.name,
      type: "classifier",
    };
  },
});

type ParentInfo = {
  id: string;
  name: string;
  type: "class" | "classifier";
};
